import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ReactComponent as ArchiveTransparent } from '../../assets/svg/ArchiveTransparent.svg'
import EmojiHalo from '../../assets/svg/EmojiHalo.svg'
import { ReactComponent as LeftIcon } from '../../assets/svg/MyTaskLeft.svg'
import { emotions_heads, emotions_list } from '../../constants/Constants'
import OpenTask from '../feedsAndDashboard/OpenTasks'
import ArchiveTasks from '../feedsAndDashboard/ArchiveTask'
import TeacherFeedbackModal from '../modals/TeacherFeedbackModal'
import HowAreYouModal from '../modals/HowAreYou/HowAreYouModal'
import { checkIfTakeDailyFeedback, getCheckUsersFeedback, sendRejectReview } from '../../server/server'
import { useTranslation } from 'react-i18next'
import SuccessModal from '../modals/SuccessModal'
import { prefersReducedMotion } from '../../utils/util'

const HALO_STYLE = { backgroundImage: `url(${EmojiHalo})` }
const SCROLL_STEP = 150
const EDGE_TOLERANCE = 1
const ARROW_ACTIVE = '#FFFFFF'
const ARROW_DISABLED = '#555BA7'
const SUCCESS_MODAL_MS = 3500

/**
 * One mood head. Hover is pure CSS (.dsh-emotion), so hovering never
 * re-renders the strip; memoized so the parent's other state changes don't
 * touch the twelve heads either.
 */
const EmotionHead = React.memo(function EmotionHead({ emotion, label, onSelect }) {
    return (
        <button type="button" className="dsh-emotion" id={`emotion-head-${emotion}`} onClick={() => onSelect(emotion)} aria-label={label}>
            <div className="dsh-emotion-content">
                <img src={emotions_heads[emotion]} alt="" draggable="false" />
                <p className="capitalize text-sm max-w-[75px] truncate">{label}</p>
            </div>
            <div className="dsh-emotion-halo" style={HALO_STYLE} aria-hidden="true" />
        </button>
    )
})

function MyTask({ user }) {
    const { t } = useTranslation()
    const [selectedState, setSelectedState] = useState('openTasks')
    const [howAreYouModalOpen, setHowAreYouModalOpen] = useState(false)
    const [openTasksNumber, setOpenTasksNumber] = useState(0)
    const [lastEmotion, setLastEmotion] = useState('sad')
    const [selectedEmotion, setSelectedEmotion] = useState('')
    const [edges, setEdges] = useState({ atStart: true, atEnd: false })
    const [teacherModalOpen, setTeacherModalOpen] = useState(false)
    const [successModalOpen, setSuccessModalOpen] = useState(false)

    const stripRef = useRef(null)
    const successTimer = useRef()
    const mountedRef = useRef(true)

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
            clearTimeout(successTimer.current)
        }
    }, [])

    const userId = user?.id
    const loadDailyFeedbackResults = useCallback(async () => {
        const resp = await checkIfTakeDailyFeedback(userId)
        if (!mountedRef.current) return
        if (resp?.status === 200) {
            setLastEmotion(resp.data)
            setSelectedEmotion('')
        }
    }, [userId])

    useEffect(() => {
        loadDailyFeedbackResults()
    }, [loadDailyFeedbackResults])

    // Arrow state is derived from the strip's real scroll position, on scroll
    // and whenever the strip resizes (sidebar pin changes its width).
    const updateEdges = useCallback(() => {
        const el = stripRef.current
        if (!el) return
        const maxScroll = el.scrollWidth - el.clientWidth
        const next = { atStart: el.scrollLeft <= EDGE_TOLERANCE, atEnd: maxScroll - el.scrollLeft <= EDGE_TOLERANCE }
        setEdges((prev) => (prev.atStart === next.atStart && prev.atEnd === next.atEnd ? prev : next))
    }, [])

    useLayoutEffect(() => {
        updateEdges()
        const el = stripRef.current
        if (!el || typeof ResizeObserver === 'undefined') return undefined
        const observer = new ResizeObserver(updateEdges)
        observer.observe(el)
        return () => observer.disconnect()
    }, [updateEdges])

    const scrollStrip = (direction) => {
        stripRef.current?.scrollBy({ left: direction * SCROLL_STEP, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
    }

    const toggleTaskState = (state) => {
        setSelectedState((prev) => (prev === 'archiveTasks' && state === 'archiveTasks' ? 'openTasks' : state))
    }

    const openHowAreYouModal = useCallback((emotion) => {
        setSelectedEmotion(emotion)
        setHowAreYouModalOpen(true)
    }, [])
    const closeHowAreYouModal = () => {
        setHowAreYouModalOpen(false)
        // HowAreYouModal does not report the submitted emotion back; refetch
        // is how the strip learns about it.
        loadDailyFeedbackResults()
    }

    const checkUsersFeedbackNeeded = useCallback(async () => {
        const resp = await getCheckUsersFeedback()
        if (mountedRef.current && resp?.status === 200 && resp.data) setTeacherModalOpen(true)
    }, [])
    const closeTeacherFeedbackModal = () => setTeacherModalOpen(false)
    const rejectReview = () => sendRejectReview()

    const openSuccessModal = () => {
        clearTimeout(successTimer.current)
        setSuccessModalOpen(true)
        successTimer.current = setTimeout(() => {
            if (mountedRef.current) setSuccessModalOpen(false)
        }, SUCCESS_MODAL_MS)
    }
    const closeSuccessModal = () => {
        clearTimeout(successTimer.current)
        setSuccessModalOpen(false)
    }

    return (
        <div className="my-tasks-widget relative xxs:w-full xl:w-[480px] flex justify-center h-full">
            <div className="bg-white h-full rounded-xl w-full flex flex-col">
                <div className="w-full flex justify-between items-center">
                    <div
                        className="rounded-xl flex-1 flex flex-col items-center justify-between min-h-[185px]"
                        style={{ background: 'linear-gradient(152.02deg, #716EBF 16.55%, #5259A4 81.86%), #413F5E' }}
                    >
                        <div className="how-do-you-feel-container w-full px-[35px] pt-5">
                            <div className="flex flex-col justify-center gap-1">
                                <p className="font-bold leading-[20px] text-[16px] text-left text-white">{t('howAreYouToday')}</p>
                                <p className="text-[#BEBECE] text-sm font-medium">{t('selectEmotionThatBestDescribesYourMoodToday')}</p>
                            </div>
                        </div>
                        <div className="w-full justify-start items-center relative h-[115px]">
                            <div className="how-do-you-feel-container-emotions absolute top-[7px] left-0 right-0 m-[0_auto] w-[90%] flex items-start justify-between">
                                <button
                                    type="button"
                                    className="dsh-strip-arrow mt-4 w-[50px] flex items-center justify-center h-[50px] rounded-full min-w-[50px] border-[5px] border-[#6364B3]"
                                    style={{ background: 'linear-gradient(180deg, #7172BA 0%, #7D7EC2 100%)', paddingRight: 4 }}
                                    onClick={() => scrollStrip(-1)}
                                    disabled={edges.atStart}
                                    aria-label={t('Previous')}
                                >
                                    <LeftIcon fill={edges.atStart ? ARROW_DISABLED : ARROW_ACTIVE} />
                                </button>
                                <div ref={stripRef} className="dsh-emotion-strip px-4 noScroll flex gap-6 overflow-y-hidden overflow-x-auto flex-nowrap" onScroll={updateEdges}>
                                    {emotions_list.map((emotion) => (
                                        <EmotionHead key={emotion} emotion={emotion} label={t(emotion)} onSelect={openHowAreYouModal} />
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className="dsh-strip-arrow mt-4 w-[50px] flex items-center justify-center h-[50px] rounded-full min-w-[50px] border-[5px] border-[#555BA7]"
                                    style={{ background: 'radial-gradient(82% 82% at 50% 9%, #646AAE 0%, #6F75B7 100%)', paddingLeft: 4 }}
                                    onClick={() => scrollStrip(1)}
                                    disabled={edges.atEnd}
                                    aria-label={t('Next')}
                                >
                                    <LeftIcon fill={edges.atEnd ? ARROW_DISABLED : ARROW_ACTIVE} style={{ transform: 'scaleX(-1)' }} />
                                </button>
                            </div>
                            <div className="h-[50px]"></div>
                            <div
                                className="w-full rounded-xl flex justify-center h-[70%]"
                                style={{
                                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.09) 0%, rgba(255, 255, 255, 0) 100%)',
                                }}
                            ></div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center mt-5 mx-[35px]">
                    <button
                        type="button"
                        className={`font-bold text-[20px] mr-[5px] transition-colors ${selectedState === 'openTasks' ? 'text-darkPurple' : 'text-mudPurple'}`}
                        onClick={() => toggleTaskState('openTasks')}
                    >
                        {t('yourTasks')}
                    </button>
                    {!!openTasksNumber && (
                        <div className="h-[31px] w-[31px] font-[600] rounded-full flex justify-center items-center border border-deselectPurple text-[14px] text-deepPurple">
                            <span key={openTasksNumber} className="dsh-badge-count">
                                {openTasksNumber}
                            </span>
                        </div>
                    )}
                    <div className="flex-1"></div>
                    <button
                        type="button"
                        className={`flex items-center font-bold text-[16px] transition-colors ${selectedState === 'archiveTasks' ? 'text-darkPurple' : 'text-mudPurple'}`}
                        onClick={() => toggleTaskState('archiveTasks')}
                        aria-pressed={selectedState === 'archiveTasks'}
                    >
                        <ArchiveTransparent style={{ color: selectedState === 'archiveTasks' ? '#413F5E' : '#9392AD' }} />
                        <span className="ml-[10px]">{t('archive')}</span>
                    </button>
                </div>
                <div className="mt-[10px] mx-[35px]">
                    <p className="text-[14px] text-[#413F5E] font-[500]">{t('activitiesForCompletion')}</p>
                </div>
                <div className="mt-5 mx-[35px] h-[1px] border-b border-dashed border-b-lightBG"></div>

                <div className="flex flex-col flex-auto mx-[10px] bg-white h-full">
                    {selectedState === 'openTasks' ? (
                        <OpenTask user={user} onCountChange={setOpenTasksNumber} checkUsersFeedbackNeeded={checkUsersFeedbackNeeded} />
                    ) : (
                        <ArchiveTasks user={user} onGoToTasks={() => toggleTaskState('openTasks')} />
                    )}
                </div>
            </div>

            <HowAreYouModal
                modalIsOpen={howAreYouModalOpen}
                closeModal={closeHowAreYouModal}
                user={user}
                lastEmotion={lastEmotion}
                setLastEmotion={setLastEmotion}
                emotionDef={selectedEmotion}
                checkUsersFeedbackNeeded={checkUsersFeedbackNeeded}
            />
            <TeacherFeedbackModal modalIsOpen={teacherModalOpen} closeModal={closeTeacherFeedbackModal} openSuccessModal={openSuccessModal} rejectReview={rejectReview} />
            <SuccessModal
                modalIsOpen={successModalOpen}
                closeModal={closeSuccessModal}
                message={t('Success')}
                subMessage={t('Thank you for helping us to make Upstrive better.')}
                blur="teacherFeedback"
            />
        </div>
    )
}

export default MyTask
